/**
 * MagicEdenCampaignProgress Component
 *
 * Displays campaign progress information:
 * - "Earned X/20,000 Points" progress
 * - Campaign end date and countdown
 *
 * Only visible when user is a Magic Eden $ME staker.
 * Designed to be subtle but clearly visible.
 */

import { useMemo } from 'react';

import { useMagicEdenCampaign } from '@/hooks/useMagicEdenCampaign';

// Magic Eden brand color
const ME_COLOR = '#E42575';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Campaign ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  return `${hours}h left`;
}

export function MagicEdenCampaignProgress() {
  const { campaignData, isLoading } = useMagicEdenCampaign();

  const timeRemaining = useMemo(() => {
    if (!campaignData?.endDate) return '';
    return formatTimeRemaining(campaignData.endDate);
  }, [campaignData?.endDate]);

  // Don't show if loading or campaign not active or user is not a staker
  if (isLoading || !campaignData?.isActive || !campaignData?.isMagicEdenStaker) {
    return null;
  }

  return (
    <div className="px-4 py-2">
      <div
        className="flex flex-col gap-1 px-3 py-2 rounded-lg text-xs"
        style={{
          backgroundColor: 'rgba(228, 37, 117, 0.08)',
          border: `1px solid rgba(228, 37, 117, 0.3)`,
          fontFamily: 'Outfit, sans-serif',
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <span style={{ color: '#999' }}>Magic Eden Bonus</span>
          <span style={{ color: ME_COLOR, fontWeight: 600 }}>
            {campaignData.userBonusEarned.toLocaleString()}/{campaignData.maxBonus.toLocaleString()} pts
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span style={{ color: '#666', fontSize: '10px' }}>Campaign ends</span>
          <span style={{ color: ME_COLOR, fontSize: '10px' }}>
            {timeRemaining}
          </span>
        </div>
      </div>
    </div>
  );
}
